import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import api from '../api/axios';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

const StaffGallery = () => {
    const [activeTab, setActiveTab] = useState('view'); // 'view' | 'upload'
    
    // View Gallery state
    const [galleryImages, setGalleryImages] = useState([]);
    const [isLoadingGallery, setIsLoadingGallery] = useState(true);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [selectedLightboxImage, setSelectedLightboxImage] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isDownloadingBatch, setIsDownloadingBatch] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0 });

    // Upload state
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
    const [uploadHistory, setUploadHistory] = useState([]);

    // Fetch images from server
    const fetchGallery = useCallback(async () => {
        setIsLoadingGallery(true);
        try {
            const response = await api.get('/gallery');
            setGalleryImages(response.data || []);
        } catch (error) {
            console.error("Error fetching staff gallery:", error);
        } finally {
            setIsLoadingGallery(false);
        }
    }, []);

    useEffect(() => {
        fetchGallery();
    }, [fetchGallery]);

    // Selection handlers
    const toggleSelect = (id, e) => {
        if (e) e.stopPropagation();
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const handleSelectAll = () => {
        if (selectedIds.size === filteredImages.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(filteredImages.map(img => img.id)));
        }
    };

    const handleDeselectAll = () => {
        setSelectedIds(new Set());
    };

    // Helper for downloading single image safely
    const downloadSingleImage = async (url, filename) => {
        try {
            const response = await fetch(url, { mode: 'cors' });
            if (!response.ok) throw new Error('Network response was not ok');
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename || 'imagen-galeria.jpg';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
        } catch (err) {
            console.warn('Direct blob fetch failed, triggering direct link download:', err);
            const link = document.createElement('a');
            link.href = url;
            link.target = '_blank';
            link.download = filename || 'imagen-galeria.jpg';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    // Download selected images as ZIP
    const handleDownloadSelected = async () => {
        if (selectedIds.size === 0) return;

        setIsDownloadingBatch(true);
        const selectedList = filteredImages.filter(img => selectedIds.has(img.id));
        setDownloadProgress({ current: 0, total: selectedList.length });

        try {
            const zip = new JSZip();
            const folder = zip.folder("galeria_seleccionada");

            for (let i = 0; i < selectedList.length; i++) {
                const img = selectedList[i];
                setDownloadProgress({ current: i + 1, total: selectedList.length });
                const fileName = img.original_name || img.filename || `foto-evento-${img.id}.jpg`;
                
                try {
                    const response = await fetch(img.display_url, { mode: 'cors' });
                    if (response.ok) {
                        const blob = await response.blob();
                        folder.file(fileName, blob);
                    } else {
                        console.warn("Failed to fetch image:", response.status);
                    }
                } catch (error) {
                    console.error("Error fetching image for zip:", img.display_url, error);
                }
            }

            const content = await zip.generateAsync({ type: "blob" });
            saveAs(content, "galeria_seleccionada.zip");
        } catch (error) {
            console.error("Error generating zip:", error);
            alert("Hubo un error al generar el archivo ZIP.");
        } finally {
            setIsDownloadingBatch(false);
            setDownloadProgress({ current: 0, total: 0 });
        }
    };

    // Delete single image
    const handleDeleteImage = async (id, e) => {
        if (e) e.stopPropagation();
        if (!window.confirm('¿Estás seguro de eliminar esta imagen de la galería?')) return;

        try {
            await api.delete(`/gallery/${id}`);
            setGalleryImages(prev => prev.filter(img => img.id !== id));
            setSelectedIds(prev => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        } catch (err) {
            console.error('Error deleting image:', err);
            alert('Error al eliminar la imagen');
        }
    };

    // Upload sequential handler
    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        setSelectedFiles(files);
        setUploadProgress({ current: 0, total: files.length });
    };

    const uploadSequentially = async () => {
        if (selectedFiles.length === 0) return;

        setIsUploading(true);
        const total = selectedFiles.length;
        const newHistory = [];

        for (let i = 0; i < selectedFiles.length; i++) {
            const file = selectedFiles[i];
            setUploadProgress({ current: i + 1, total });

            const formData = new FormData();
            formData.append('image', file);

            try {
                const response = await api.post('/gallery/upload', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                newHistory.unshift({
                    name: file.name,
                    status: 'success',
                    url: response.data.url,
                    time: new Date().toLocaleTimeString()
                });
            } catch (error) {
                console.error(`Error uploading ${file.name}:`, error);
                newHistory.unshift({
                    name: file.name,
                    status: 'error',
                    message: error.message,
                    time: new Date().toLocaleTimeString()
                });
            }
            await new Promise(r => setTimeout(r, 400));
        }

        setUploadHistory(prev => [...newHistory, ...prev]);
        setIsUploading(false);
        setSelectedFiles([]);
        setUploadProgress({ current: 0, total: 0 });
        alert('Proceso de carga finalizado exitosamente');
        
        // Refresh gallery images
        fetchGallery();
    };

    // Filter images by search term
    const filteredImages = galleryImages.filter(img => {
        if (!searchTerm.trim()) return true;
        const term = searchTerm.toLowerCase();
        return (
            (img.original_name && img.original_name.toLowerCase().includes(term)) ||
            (img.upload_date && img.upload_date.includes(term))
        );
    });

    return (
        <div className="animate-fadeIn pb-20">
            {/* Page Header */}
            <header className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <div className="inline-block p-3 bg-white rounded-2xl shadow-md border border-slate-100 text-3xl mb-2">
                        📸
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
                        Galería Staff
                    </h1>
                    <p className="text-slate-500 font-medium text-xs sm:text-sm mt-1">
                        Visualiza, selecciona y descarga fotos del evento o sube nuevo contenido.
                    </p>
                </div>

                {/* Tab Controls */}
                <div className="flex bg-slate-100 p-1.5 rounded-2xl self-start md:self-auto border border-slate-200/60 shadow-inner">
                    <button
                        onClick={() => setActiveTab('view')}
                        className={`px-5 py-2.5 rounded-xl font-black text-xs uppercase tracking-wider transition-all flex items-center gap-2 ${
                            activeTab === 'view'
                                ? 'bg-white text-slate-900 shadow-md scale-[1.02]'
                                : 'text-slate-500 hover:text-slate-800'
                        }`}
                    >
                        🖼️ Galería ({galleryImages.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('upload')}
                        className={`px-5 py-2.5 rounded-xl font-black text-xs uppercase tracking-wider transition-all flex items-center gap-2 ${
                            activeTab === 'upload'
                                ? 'bg-white text-slate-900 shadow-md scale-[1.02]'
                                : 'text-slate-500 hover:text-slate-800'
                        }`}
                    >
                        📤 Cargar Fotos
                    </button>
                </div>
            </header>

            {/* TAB 1: VISUALIZACIÓN Y DESCARGA */}
            {activeTab === 'view' && (
                <div className="space-y-6">
                    {/* Control Bar: Search + Batch Download Controls */}
                    <div className="bg-white p-4 sm:p-6 rounded-3xl border border-slate-100 shadow-xl shadow-slate-200/50 flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-4">
                        {/* Search Bar */}
                        <div className="relative flex-1">
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">🔍</span>
                            <input
                                type="text"
                                placeholder="Buscar por nombre o fecha (AAAA-MM-DD)..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                            />
                        </div>

                        {/* Batch Action Controls */}
                        <div className="flex flex-wrap items-center gap-3">
                            <button
                                onClick={handleSelectAll}
                                className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-extrabold text-[11px] uppercase tracking-wider rounded-xl transition-all"
                            >
                                {selectedIds.size === filteredImages.length && filteredImages.length > 0
                                    ? 'Desmarcar Todas'
                                    : 'Seleccionar Todas'}
                            </button>

                            {selectedIds.size > 0 && (
                                <button
                                    onClick={handleDeselectAll}
                                    className="px-3 py-2.5 bg-red-50 hover:bg-red-100 text-red-600 font-bold text-[11px] rounded-xl transition-all"
                                >
                                    Limpiar ({selectedIds.size})
                                </button>
                            )}

                            <button
                                onClick={handleDownloadSelected}
                                disabled={selectedIds.size === 0 || isDownloadingBatch}
                                className={`px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all shadow-lg flex items-center gap-2 ${
                                    selectedIds.size > 0 && !isDownloadingBatch
                                        ? 'bg-blue-600 hover:bg-blue-700 text-white hover:scale-105 active:scale-95 shadow-blue-500/25'
                                        : 'bg-slate-200 text-slate-400 cursor-not-allowed shadow-none'
                                }`}
                            >
                                {isDownloadingBatch ? (
                                    <>
                                        <span className="animate-spin text-sm">⏳</span>
                                        <span>Descargando {downloadProgress.current}/{downloadProgress.total}...</span>
                                    </>
                                ) : (
                                    <>
                                        <span>📥 Descargar Seleccionadas (ZIP)</span>
                                        {selectedIds.size > 0 && (
                                            <span className="bg-white/20 px-2 py-0.5 rounded-full text-[10px]">
                                                {selectedIds.size}
                                            </span>
                                        )}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Progress indicator during batch download */}
                    {isDownloadingBatch && (
                        <div className="bg-blue-50 border border-blue-200 p-4 rounded-2xl">
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-xs font-bold text-blue-800 uppercase">Descargando por lotes...</span>
                                <span className="text-xs font-bold text-blue-600">{downloadProgress.current} de {downloadProgress.total}</span>
                            </div>
                            <div className="h-3 bg-blue-100 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-blue-600 transition-all duration-300 rounded-full"
                                    style={{ width: `${(downloadProgress.current / downloadProgress.total) * 100}%` }}
                                ></div>
                            </div>
                        </div>
                    )}

                    {/* Gallery Grid */}
                    {isLoadingGallery ? (
                        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl border border-slate-100">
                            <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                            <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mt-4">
                                Cargando imágenes de la galería...
                            </p>
                        </div>
                    ) : filteredImages.length > 0 ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                            {filteredImages.map((img) => {
                                const isSelected = selectedIds.has(img.id);
                                return (
                                    <div
                                        key={img.id}
                                        onClick={(e) => toggleSelect(img.id, e)}
                                        className={`group relative rounded-3xl overflow-hidden bg-slate-100 border-2 transition-all duration-300 cursor-pointer shadow-md hover:shadow-xl ${
                                            isSelected
                                                ? 'border-blue-600 ring-4 ring-blue-500/20 scale-[1.02]'
                                                : 'border-white hover:border-blue-200'
                                        }`}
                                    >
                                        {/* Image Aspect Ratio Box */}
                                        <div className="aspect-square relative overflow-hidden bg-slate-900">
                                            <img
                                                src={img.display_url}
                                                alt={img.original_name || 'Foto de galería'}
                                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                                loading="lazy"
                                            />

                                            {/* Selection Checkbox Overlay Badge */}
                                            <div
                                                onClick={(e) => toggleSelect(img.id, e)}
                                                className={`absolute top-3 left-3 z-10 w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all shadow-md ${
                                                    isSelected
                                                        ? 'bg-blue-600 border-white text-white scale-110'
                                                        : 'bg-black/40 backdrop-blur-md border-white/70 text-transparent hover:bg-black/60 hover:text-white/50'
                                                }`}
                                                title={isSelected ? 'Desmarcar' : 'Seleccionar'}
                                            >
                                                ✓
                                            </div>

                                            {/* Top Right Quick Download & Delete Actions */}
                                            <div className="absolute top-3 right-3 z-10 flex items-center gap-1.5 opacity-90 sm:opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        downloadSingleImage(img.display_url, img.original_name || `foto-${img.id}.jpg`);
                                                    }}
                                                    className="w-8 h-8 rounded-full bg-slate-900/80 hover:bg-blue-600 text-white backdrop-blur-md flex items-center justify-center text-xs shadow-md transition-all hover:scale-110"
                                                    title="Descargar imagen"
                                                >
                                                    💾
                                                </button>
                                                <button
                                                    onClick={(e) => handleDeleteImage(img.id, e)}
                                                    className="w-8 h-8 rounded-full bg-slate-900/80 hover:bg-red-600 text-white backdrop-blur-md flex items-center justify-center text-xs shadow-md transition-all hover:scale-110"
                                                    title="Eliminar imagen"
                                                >
                                                    🗑️
                                                </button>
                                            </div>

                                            {/* Hover View Lightbox Overlay */}
                                            <div 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedLightboxImage(img);
                                                }}
                                                className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center p-3"
                                            >
                                                <span className="text-[10px] font-black uppercase tracking-wider bg-white/20 backdrop-blur-md text-white px-3 py-1.5 rounded-full hover:bg-white/40 transition-colors">
                                                    🔍 Ampliar
                                                </span>
                                            </div>
                                        </div>

                                        {/* Card Footer Info */}
                                        <div className="p-3 bg-white flex flex-col justify-between border-t border-slate-100">
                                            <p className="text-[11px] font-bold text-slate-800 truncate" title={img.original_name}>
                                                {img.original_name || img.filename}
                                            </p>
                                            <p className="text-[9px] font-semibold text-slate-400 mt-0.5">
                                                📅 {img.upload_date || 'Sin fecha'}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-center py-20 bg-white rounded-3xl border border-slate-100 p-8 shadow-sm">
                            <span className="text-5xl opacity-40">🖼️</span>
                            <p className="text-slate-500 font-bold text-xs uppercase tracking-wider mt-4">
                                No se encontraron imágenes en la galería
                            </p>
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    className="mt-3 text-xs font-bold text-blue-600 hover:underline"
                                >
                                    Limpiar filtro de búsqueda
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* TAB 2: CARGA DE FOTOS */}
            {activeTab === 'upload' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Upload Dropzone */}
                    <div className="bg-white p-8 sm:p-10 rounded-[40px] border border-slate-100 shadow-xl shadow-slate-200/50">
                        <div
                            className={`relative border-4 border-dashed rounded-[35px] p-10 flex flex-col items-center justify-center transition-all ${
                                isUploading ? 'border-blue-200 bg-blue-50/20' : 'border-slate-200 hover:border-blue-300 bg-slate-50/50'
                            }`}
                        >
                            <input
                                type="file"
                                multiple
                                accept="image/*"
                                onChange={handleFileSelect}
                                disabled={isUploading}
                                className="absolute inset-0 opacity-0 cursor-pointer"
                            />
                            <span className="text-5xl mb-4">📂</span>
                            <p className="text-slate-600 font-black text-xs uppercase tracking-widest text-center">
                                {selectedFiles.length > 0
                                    ? `${selectedFiles.length} archivos seleccionados`
                                    : 'Arrastra o haz clic para seleccionar fotos'}
                            </p>
                            <p className="text-slate-400 text-[10px] font-medium text-center mt-2">
                                Se aplicará la marca de agua corporativa de forma automática.
                            </p>
                        </div>

                        {selectedFiles.length > 0 && !isUploading && (
                            <button
                                onClick={uploadSequentially}
                                className="w-full mt-8 py-4 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white font-black uppercase tracking-widest text-xs transition-all flex items-center justify-center gap-3 shadow-lg shadow-blue-500/30 hover:scale-[1.01]"
                            >
                                Comenzar Carga Secuencial 🚀
                            </button>
                        )}

                        {isUploading && (
                            <div className="mt-8 space-y-4">
                                <div className="flex justify-between items-end mb-2">
                                    <span className="text-[10px] font-black uppercase tracking-widest text-blue-600">Procesando e Subiendo...</span>
                                    <span className="text-[10px] font-black text-slate-500">{uploadProgress.current} / {uploadProgress.total}</span>
                                </div>
                                <div className="h-4 bg-slate-100 rounded-full overflow-hidden p-1 border border-slate-200">
                                    <div
                                        className="h-full bg-blue-600 rounded-full transition-all duration-500 shadow-inner"
                                        style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                                    ></div>
                                </div>
                                <p className="text-center text-[10px] font-bold text-slate-400 animate-pulse">
                                    Aplicando marca de agua y optimizando dimensiones...
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Recent Upload History */}
                    <div className="space-y-4">
                        <h3 className="text-xs font-black uppercase tracking-widest text-slate-400 px-2">
                            Fotos Recién Subidas
                        </h3>
                        <div className="max-h-[500px] overflow-y-auto pr-2 space-y-3 custom-scrollbar">
                            {uploadHistory.length > 0 ? (
                                uploadHistory.map((item, idx) => (
                                    <div key={idx} className="bg-white p-4 rounded-3xl border border-slate-100 flex items-center gap-4 shadow-sm animate-slideIn">
                                        <div className={`w-10 h-10 rounded-2xl flex items-center justify-center text-xl shadow-sm ${
                                            item.status === 'success' ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'
                                        }`}>
                                            {item.status === 'success' ? '✅' : '❌'}
                                        </div>
                                        <div className="flex-1 overflow-hidden">
                                            <p className="text-xs font-bold text-slate-800 truncate">{item.name}</p>
                                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-tighter">{item.time}</p>
                                        </div>
                                        {item.status === 'success' && (
                                            <a
                                                href={item.url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="px-3 py-1.5 bg-slate-50 hover:bg-blue-50 hover:text-blue-600 rounded-xl text-[9px] font-black uppercase transition-colors"
                                            >
                                                Ver ↗
                                            </a>
                                        )}
                                    </div>
                                ))
                            ) : (
                                <div className="bg-slate-50/50 border border-dashed border-slate-200 rounded-[35px] py-20 flex flex-col items-center justify-center opacity-50">
                                    <span className="text-2xl grayscale">⌛</span>
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-2">
                                        No hay subidas recientes en esta sesión
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Premium Lightbox Preview Modal */}
            {selectedLightboxImage && createPortal(
                <div
                    className="fixed inset-0 z-[99999] bg-slate-950/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-8 animate-fadeIn"
                    onClick={() => setSelectedLightboxImage(null)}
                >
                    <button
                        className="absolute top-6 right-6 w-12 h-12 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center text-2xl transition-all hover:rotate-90"
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedLightboxImage(null);
                        }}
                    >
                        ✕
                    </button>
                    <div className="max-w-5xl w-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
                        <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-white/10 bg-black">
                            <img
                                src={selectedLightboxImage.display_url}
                                alt="Vista previa"
                                className="max-h-[75vh] w-auto object-contain"
                            />
                        </div>
                        <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
                            <button
                                onClick={() => downloadSingleImage(selectedLightboxImage.display_url, selectedLightboxImage.original_name)}
                                className="bg-blue-600 hover:bg-blue-700 px-8 py-3.5 rounded-2xl shadow-xl text-white font-black uppercase tracking-widest text-xs transition-all flex items-center gap-2 hover:scale-105 cursor-pointer"
                            >
                                💾 Descargar Alta Resolución
                            </button>
                            <button
                                onClick={(e) => {
                                    handleDeleteImage(selectedLightboxImage.id, e);
                                    setSelectedLightboxImage(null);
                                }}
                                className="bg-red-600/80 hover:bg-red-600 px-6 py-3.5 rounded-2xl text-white font-extrabold uppercase tracking-wider text-xs transition-all flex items-center gap-2 cursor-pointer"
                            >
                                🗑️ Eliminar
                            </button>
                            <button
                                onClick={() => setSelectedLightboxImage(null)}
                                className="bg-slate-800 hover:bg-slate-700 border border-white/20 px-6 py-3.5 rounded-2xl text-white font-black uppercase tracking-wider text-xs transition-all flex items-center gap-2 hover:scale-105 cursor-pointer"
                            >
                                ✕ Cerrar
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default StaffGallery;

